import {
    Accordion,
    AccordionHeader,
    AccordionBody,
} from "@material-tailwind/react";
import {useTranslation} from "react-i18next";
import React, {useEffect, useState} from "react";
import InfoOnline from "../../components/InfoOnline";
import NotPermissionComponent from "../NotPermissionComponent";
import {useFindSupa} from "../../../services/apis/apiLegalInformation";
import NoRecords from "../../components/NoRecords";
import {ItemInfo} from "../../components/ViewData";
import * as IconsMui from "@mui/icons-material";
import {formatDateString} from "../../../utils/daysOptions";
import CustomTable from "../../../components/general/CustomTable";
import {Person} from "../../../services/interface/query/person";
import {SupaInterface} from "../../../services/interface/query/legal-information/supa/supa-interface";
import {AlimonyMovementInterface} from "../../../services/interface/query/legal-information/supa/alimony-movement-interface";
import {LegalCaseStatusEnum} from "../../../services/enum/LegalCaseStatusEnum";

interface Props {
    sri?: boolean;
    person?: Person;
}

export default function Supa({sri, person}: Props) {
    const { t } = useTranslation("global") as { t: (key: string) => string };

    const {
        mutate: findSupa,
        data: successLegalInformation,
        error: errorLegalInformation,
        isPending: loadingLegalInformation,
    } = useFindSupa();

    const [response, setResponse] = useState<SupaInterface[] | undefined>();
    const [openAccordion, setOpenAccordion] = useState<number | null>(null);

    const handleOpen = (index: number) => {
        setOpenAccordion(openAccordion === index ? null : index);
    };

    const find = () => {
        if (person && person.identification && !sri) {
            setResponse(undefined);
            findSupa({identifier: person.identification, msj: t('supa')});
        } else if (sri && person && person?.taxIdentificationNumber) {
            setResponse(undefined);
            findSupa({identifier: person?.taxIdentificationNumber, msj: t('supa')});
        }
    };

    useEffect(() => {
        find();
    }, [person]);

    useEffect(() => {
        if (successLegalInformation) {
            setResponse(successLegalInformation);
            if (successLegalInformation.length > 0) {
                setOpenAccordion(0);
            }
        }
    }, [successLegalInformation]);

    const amount = (value?: number) => (
        value === undefined || value === null ? '-' : `$${value.toFixed(2)}`
    );

    const amountCell = (value?: number) => (
        <span className={'flex justify-end font-light text-[12px]'}>{amount(value)}</span>
    );

    const isInArrears = (item: SupaInterface) => item.statusLegalCase === LegalCaseStatusEnum.IN_ARREARS;

    const statusChip = (item: SupaInterface) => (
        <div className={'flex flex-row bg-white pr-2 gap-1 rounded-[4px] items-center max-h-[30px] w-fit'}>
            <div className={`rounded-l-[10px] min-w-[6px] max-w-[6px] h-[30px] 
            ${isInArrears(item) ? 'bg-yellow-300' : 'bg-success'}`}/>
            <span className={'text-[14px] font-light'}>
                {isInArrears(item) ? t('in-arrears') : t('up-to-date')}
            </span>
        </div>
    );

    const joinParties = (item: SupaInterface, role: 'actor' | 'defendant') => {
        const names = (item.actions || [])
            .flatMap((action) => action[role] || [])
            .map((party) => party.names)
            .filter((name) => !!name);
        return names.length > 0 ? names.join(', ') : undefined;
    };

    const movementsTable = (movements: AlimonyMovementInterface[], index: number, suffix: string) => (
        <CustomTable
            key={`table-supa-${suffix}-${index}`}
            header={[
                t('number'),
                t('year'),
                t('month'),
                t('concept'),
                t('status'),
                t('due-date'),
                t('debt'),
                t('paid-value'),
            ]}
            listElement={movements.map((movement) => ({
                row: [
                    movement.number || '-',
                    movement.year || '-',
                    movement.month || '-',
                    movement.concept || '-',
                    movement.status || '-',
                    formatDateString(movement.dueDate),
                    amountCell(movement.debtValue),
                    amountCell(movement.paidValue),
                ]
            }))}
        />
    );

    return (
        <div className="flex flex-col gap-3">
            <h2 className="text-center text-lg font-semibold text-gray-900 mb-6 w-full">
                {t("supa-description")}
            </h2>

            <div className="flex flex-row gap-2">
                <h3>{t('supa')}</h3>
                <InfoOnline onClick={find}/>
            </div>

            <NotPermissionComponent
                title={'/legal_cases/alimony'}
                loading={loadingLegalInformation}
                error={errorLegalInformation}
                component={
                    <>
                        {!loadingLegalInformation && response && response.length > 0 ? (
                            response.map((item, index) => (
                                <Accordion
                                    key={`accordion-supa-${index}`}
                                    open={openAccordion === index}
                                    icon={<IconsMui.ExpandMore/>}
                                    placeholder=""
                                >
                                    <AccordionHeader onClick={() => handleOpen(index)} placeholder="">
                                        <div className="flex flex-row items-center gap-3">
                                            <div className="text-base">
                                                {`${t('alimony')} #${index + 1}`}
                                            </div>
                                            {statusChip(item)}
                                        </div>
                                    </AccordionHeader>
                                    <AccordionBody>
                                        <div className="custom-card p-3 flex flex-col gap-3">

                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                                <ItemInfo
                                                    label={t('code-card')}
                                                    text={item.alimony?.cardCode}
                                                    icon={<IconsMui.CreditCardTwoTone/>}
                                                    classNameValue="text-sm"
                                                />
                                                <ItemInfo
                                                    label={t('nro-process')}
                                                    text={item.processNumber}
                                                    icon={<IconsMui.ReceiptLongTwoTone/>}
                                                    classNameValue="text-sm"
                                                />
                                                <ItemInfo
                                                    label={t('pension-type')}
                                                    text={item.nameCase}
                                                    icon={<IconsMui.AccountBalanceTwoTone/>}
                                                    classNameValue="text-sm"
                                                />
                                                <ItemInfo
                                                    label={t('current-pension')}
                                                    text={amount(item.alimony?.pensionValue)}
                                                    icon={<IconsMui.PaymentsTwoTone/>}
                                                    classNameValue="text-sm"
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-3 gap-3">
                                                <ItemInfo
                                                    label={t('judiciary')}
                                                    text={item.location}
                                                    icon={<IconsMui.FmdGoodTwoTone/>}
                                                    classNameValue="text-sm"
                                                />
                                                <ItemInfo
                                                    label={t('main-obligor')}
                                                    text={joinParties(item, 'defendant')}
                                                    icon={<IconsMui.PersonTwoTone/>}
                                                    classNameValue="text-sm"
                                                />
                                                <ItemInfo
                                                    label={t('legal-representative')}
                                                    text={joinParties(item, 'actor')}
                                                    icon={<IconsMui.SupervisorAccountTwoTone/>}
                                                    classNameValue="text-sm"
                                                />
                                            </div>

                                            <div className="capitalize text-empColorPrimary font-semibold -mb-2 text-[14px]">
                                                {t('summary')}
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                                <ItemInfo
                                                    label={t('paid-movements')}
                                                    text={item.alimony?.paidMovementsNumber}
                                                    icon={<IconsMui.PlaylistAddCheckTwoTone/>}
                                                    classNameValue="text-sm"
                                                />
                                                <ItemInfo
                                                    label={t('total-debt')}
                                                    text={amount(item.alimony?.totalDebt)}
                                                    icon={<IconsMui.RequestQuoteTwoTone/>}
                                                    classNameValue="text-sm"
                                                />
                                                <ItemInfo
                                                    label={t('total-paid')}
                                                    text={amount(item.alimony?.totalPaid)}
                                                    icon={<IconsMui.PriceCheckTwoTone/>}
                                                    classNameValue="text-sm"
                                                />
                                                <ItemInfo
                                                    label={t('total-pending')}
                                                    text={amount(item.alimony?.totalPending)}
                                                    icon={<IconsMui.MoneyOffTwoTone/>}
                                                    classNameValue="text-sm"
                                                />
                                            </div>

                                            {isInArrears(item) && (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                                    <ItemInfo
                                                        label={t('pending-pensions')}
                                                        text={item.alimony?.pendingPensionsNumber}
                                                        icon={<IconsMui.EventBusyTwoTone/>}
                                                        classNameValue="text-sm"
                                                    />
                                                    <ItemInfo
                                                        label={t('subtotal-pensions')}
                                                        text={amount(item.alimony?.subtotalPensions)}
                                                        icon={<IconsMui.RequestQuoteTwoTone/>}
                                                        classNameValue="text-sm"
                                                    />
                                                    <ItemInfo
                                                        label={t('subtotal-interests')}
                                                        text={amount(item.alimony?.subtotalInterests)}
                                                        icon={<IconsMui.PercentTwoTone/>}
                                                        classNameValue="text-sm"
                                                    />
                                                    <ItemInfo
                                                        label={t('total-other-debts')}
                                                        text={amount(item.alimony?.totalOtherDebts)}
                                                        icon={<IconsMui.AccountBalanceWalletTwoTone/>}
                                                        classNameValue="text-sm"
                                                    />
                                                </div>
                                            )}

                                            {item.alimony?.pendingMovements && item.alimony.pendingMovements.length > 0 && (
                                                <>
                                                    <div className="capitalize text-empColorPrimary font-semibold -mb-2 text-[14px]">
                                                        {t('pending-movements')}
                                                    </div>
                                                    {movementsTable(item.alimony.pendingMovements, index, 'pending')}
                                                </>
                                            )}

                                            {item.alimony?.paidMovements && item.alimony.paidMovements.length > 0 && (
                                                <>
                                                    <div className="capitalize text-empColorPrimary font-semibold -mb-2 text-[14px]">
                                                        {t('paid-movements')}
                                                    </div>
                                                    {movementsTable(item.alimony.paidMovements, index, 'paid')}
                                                </>
                                            )}

                                        </div>
                                    </AccordionBody>
                                </Accordion>
                            ))
                        ) : (
                            <NoRecords/>
                        )}
                    </>
                }
            />
        </div>
    );
}
